/**
 * Ticket 114.3 — Pre-Sales front door: full 9-step desktop wizard.
 *
 * Requires demo.refine.advanced permission (enforced via ProtectedRoute).
 *
 * Steps:
 *   1. Country
 *   2. Industry
 *   3. Business profile
 *   4. Audience
 *   5. Theme
 *   6. Hero
 *   7. Offers
 *   8. Testimonials
 *   9. Review + Generate
 *
 * "Generate" in this phase uses a static mock — buildInitialContentMock()
 * in api/demos.ts collects inputs from industry.sample_business +
 * industry.content_schema. Phase 4 will replace or wrap this with an
 * LLM call to DemoContentService.generateInitial on the backend.
 *
 * Route: /demos/new
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Stepper, type StepperStep } from '@/components/Stepper';
import { createDemo, generateDemoContent, buildInitialContentMock, type DemoTheme } from '@/api/demos';
import { apiClient } from '@/lib/apiClient';
import { useDemoCountries } from './hooks/useDemoCountries';
import { useDemoIndustries } from './hooks/useDemoIndustries';

type AudienceMode = 'B2C' | 'B2B';

interface BusinessProfile {
  name: string;
  location: string;
  contact: string;
  tagline: string;
  valueProp: string;
  differentiator: string;
}

interface AudienceProfile {
  mode: AudienceMode;
  segments: string[];
  kpi: string;
  cta: string;
  voiceTone: string;
  integrations: string[];
}

interface HeroData {
  headline: string;
  sub: string;
  cta: string;
}

const THEMES: DemoTheme[] = ['sunburst', 'editorial', 'quantum', 'aurora'];
const TOTAL_STEPS = 9;

export function DemoStudioPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — Country
  const [country, setCountry] = useState('');

  // Step 2 — Industry
  const [industryKey, setIndustryKey] = useState('');

  // Step 3 — Business profile
  const [business, setBusiness] = useState<BusinessProfile>({
    name: '',
    location: '',
    contact: '',
    tagline: '',
    valueProp: '',
    differentiator: '',
  });

  // Step 4 — Audience
  const [audience, setAudience] = useState<AudienceProfile>({
    mode: 'B2C',
    segments: [],
    kpi: '',
    cta: '',
    voiceTone: '',
    integrations: [],
  });

  // Step 5 — Theme
  const [theme, setTheme] = useState<DemoTheme>('sunburst');

  // Step 6 — Hero
  const [hero, setHero] = useState<HeroData>({ headline: '', sub: '', cta: '' });

  // Step 7 — Offers (3 items, pre-filled from sample)
  const [offers, setOffers] = useState<string[]>(['', '', '']);

  // Step 8 — Testimonials (2-3 quotes)
  const [testimonials, setTestimonials] = useState<string[]>(['', '']);

  // 114.6 — Optional opportunity link (shown at review step)
  const [opportunityId, setOpportunityId] = useState<string>('');
  const [opportunitySearch, setOpportunitySearch] = useState<string>('');
  const [opportunitySuggestions, setOpportunitySuggestions] = useState<
    { id: string; name: string }[]
  >([]);

  const searchOpportunities = useCallback(async (q: string) => {
    if (!q.trim()) { setOpportunitySuggestions([]); return; }
    try {
      const { data } = await apiClient.get<{ id: string; name: string }[]>(
        '/opportunities',
        { params: { search: q, limit: 5 } },
      );
      setOpportunitySuggestions(data);
    } catch {
      setOpportunitySuggestions([]);
    }
  }, []);

  const { countries } = useDemoCountries();
  const { industries } = useDemoIndustries(country || undefined);

  const selectedIndustry = industries.find(
    (ind) => ind.key === industryKey && ind.country === country,
  );

  // Pre-fill from industry sample_business when industry changes
  function selectIndustry(key: string) {
    setIndustryKey(key);
    const ind = industries.find((i) => i.key === key && i.country === country);
    if (!ind) return;
    const sample = (ind.sampleBusiness ?? {}) as Record<string, Record<string, string>>;

    if (sample.business) {
      setBusiness((prev) => ({
        ...prev,
        name: sample.business?.name ?? prev.name,
        tagline: sample.business?.tagline ?? prev.tagline,
        valueProp: sample.business?.valueProp ?? prev.valueProp,
      }));
    }
    if (sample.hero) {
      setHero({
        headline: sample.hero?.headline ?? '',
        sub: sample.hero?.sub ?? '',
        cta: sample.hero?.cta ?? '',
      });
    }
    if (Array.isArray(sample.offers)) {
      setOffers((sample.offers as string[]).slice(0, 3).concat(['', '', '']).slice(0, 3));
    }
    if (Array.isArray(sample.testimonials)) {
      setTestimonials(
        (sample.testimonials as string[]).slice(0, 3).concat(['', '']).slice(0, 2),
      );
    }
  }

  // Validity per step
  const stepValid: boolean[] = [
    !!country,
    !!industryKey,
    !!business.name.trim(),
    !!audience.mode,
    !!theme,
    !!hero.headline.trim(),
    offers.some((o) => o.trim()),
    testimonials.some((t) => t.trim()),
    true, // review always enabled
  ];

  const steps: StepperStep[] = [
    { key: 'country', label: t('demos.studio.step.country.title'), valid: stepValid[0] },
    { key: 'industry', label: t('demos.studio.step.industry.title'), valid: stepValid[1] },
    { key: 'business', label: t('demos.studio.step.business.title'), valid: stepValid[2] },
    { key: 'audience', label: t('demos.studio.step.audience.title'), valid: stepValid[3] },
    { key: 'theme', label: t('demos.studio.step.theme.title'), valid: stepValid[4] },
    { key: 'hero', label: t('demos.studio.step.hero.title'), valid: stepValid[5] },
    { key: 'offers', label: t('demos.studio.step.offers.title'), valid: stepValid[6] },
    { key: 'testimonials', label: t('demos.studio.step.testimonials.title'), valid: stepValid[7] },
    { key: 'review', label: t('demos.studio.step.review.title'), valid: stepValid[8] },
  ];

  const canAdvance = stepValid[stepIndex] ?? false;
  const isLastStep = stepIndex === TOTAL_STEPS - 1;

  function handleNext() {
    if (!isLastStep && canAdvance) setStepIndex((i) => i + 1);
  }

  function handleBack() {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  }

  async function handleGenerate() {
    if (!country || !industryKey || !business.name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      // Phase 4: create the demo with static mock content first, then
      // call POST /api/v1/demos/{id}/generate to enrich with LLM content.
      // On LLM failure (502), fall back gracefully to the static mock and
      // display an informative toast.
      const content = selectedIndustry ? buildInitialContentMock(selectedIndustry) : undefined;
      const audienceJson = JSON.stringify({
        ...audience,
        location: business.location,
        tagline: business.tagline,
        valueProp: business.valueProp,
        differentiator: business.differentiator,
      });

      const demo = await createDemo({
        country,
        industryKey,
        businessName: business.name.trim(),
        theme,
        audience: audienceJson,
        content,
        opportunityId: opportunityId || null,
      });

      // Attempt LLM generation; fall back to static mock on failure
      try {
        setError(null);
        await generateDemoContent(demo.id, true);
      } catch {
        // LLM unavailable or returned 502 — static mock content already
        // persisted via createDemo; show informative fallback toast.
        setError(t('demos.studio.generate.failed.fallback'));
      }

      navigate(`/demos/${demo.id}`);
    } catch {
      setError(t('demos.studio.error.create'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <div className="border-b border-border bg-background px-6 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-xl font-semibold mb-4">{t('demos.studio.title')}</h1>
          <Stepper
            steps={steps}
            currentIndex={stepIndex}
            onStepClick={(i) => {
              if (i < stepIndex) setStepIndex(i);
            }}
          />
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 px-6 py-8">
        <div className="max-w-2xl mx-auto space-y-6">

          {/* Step 1 — Country */}
          {stepIndex === 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.country.title')}</h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {countries.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => setCountry(c.code)}
                    className={`rounded-lg border-2 p-4 text-center transition-colors
                      ${country === c.code ? 'border-[color:var(--color-lop-teal)] bg-[color:var(--color-lop-teal)]/10' : 'border-border'}`}
                    aria-pressed={country === c.code}
                  >
                    <div className="text-2xl mb-1">{countryFlag(c.code)}</div>
                    <div className="text-sm font-medium">{c.name}</div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => navigate('/admin/demos/catalog?tab=countries&action=new')}
                  className="rounded-lg border-2 border-dashed border-border p-4 text-center text-sm text-muted-foreground transition-colors hover:border-[color:var(--color-lop-teal)] hover:text-[color:var(--color-lop-teal)]"
                  data-testid="demos-studio-add-country"
                >
                  <div className="text-2xl mb-1">+</div>
                  <div className="text-sm font-medium">{t('demos.studio.step.country.addNew')}</div>
                </button>
              </div>
              <p className="text-xs text-muted-foreground">{t('demos.studio.step.country.addNewHint')}</p>
            </div>
          )}

          {/* Step 2 — Industry */}
          {stepIndex === 1 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.industry.title')}</h2>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={industryKey}
                onChange={(e) => selectIndustry(e.target.value)}
                aria-label={t('demos.studio.step.industry.title')}
              >
                <option value="">—</option>
                {industries.map((ind) => (
                  <option key={`${ind.country}-${ind.key}`} value={ind.key}>
                    {ind.name}
                    {ind.tagline ? ` — ${ind.tagline}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Step 3 — Business profile */}
          {stepIndex === 2 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.business.title')}</h2>
              {([
                ['name', t('demos.list.column.businessName')],
                ['location', t('demos.studio.step.business.location')],
                ['contact', t('demos.studio.step.business.contact')],
                ['tagline', t('demos.studio.step.business.tagline')],
                ['valueProp', t('demos.studio.step.business.valueProp')],
                ['differentiator', t('demos.studio.step.business.differentiator')],
              ] as [keyof BusinessProfile, string][]).map(([field, label]) => (
                <div key={field} className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground" htmlFor={`bp-${field}`}>
                    {label}
                  </label>
                  <Input
                    id={`bp-${field}`}
                    value={business[field]}
                    onChange={(e) => setBusiness((prev) => ({ ...prev, [field]: e.target.value }))}
                    placeholder={label}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Step 4 — Audience */}
          {stepIndex === 3 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.audience.title')}</h2>
              {/* B2C / B2B */}
              <div className="flex gap-3">
                {(['B2C', 'B2B'] as AudienceMode[]).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setAudience((a) => ({ ...a, mode }))}
                    aria-pressed={audience.mode === mode}
                    className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors
                      ${audience.mode === mode ? 'border-[color:var(--color-lop-teal)] text-[color:var(--color-lop-teal)] bg-[color:var(--color-lop-teal)]/10' : 'border-border text-muted-foreground'}`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="aud-kpi">
                  {t('demos.studio.step.audience.kpi')}
                </label>
                <Input
                  id="aud-kpi"
                  value={audience.kpi}
                  onChange={(e) => setAudience((a) => ({ ...a, kpi: e.target.value }))}
                  placeholder={t('demos.studio.step.audience.kpi')}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="aud-cta">
                  {t('demos.studio.step.audience.cta')}
                </label>
                <Input
                  id="aud-cta"
                  value={audience.cta}
                  onChange={(e) => setAudience((a) => ({ ...a, cta: e.target.value }))}
                  placeholder={t('demos.studio.step.audience.cta')}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="aud-tone">
                  {t('demos.studio.step.audience.voiceTone')}
                </label>
                <Input
                  id="aud-tone"
                  value={audience.voiceTone}
                  onChange={(e) => setAudience((a) => ({ ...a, voiceTone: e.target.value }))}
                  placeholder={t('demos.studio.step.audience.voiceTone')}
                />
              </div>
            </div>
          )}

          {/* Step 5 — Theme */}
          {stepIndex === 4 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.theme.title')}</h2>
              <div className="grid grid-cols-2 gap-3">
                {THEMES.map((th) => (
                  <button
                    key={th}
                    type="button"
                    onClick={() => setTheme(th)}
                    aria-pressed={theme === th}
                    className={`rounded-lg border-2 p-6 text-left transition-colors
                      ${theme === th ? 'border-[color:var(--color-lop-teal)] bg-[color:var(--color-lop-teal)]/10' : 'border-border'}`}
                  >
                    <span className="font-medium capitalize">{t(`demos.theme.${th}.label`)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 6 — Hero */}
          {stepIndex === 5 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.hero.title')}</h2>
              {([
                ['headline', t('demos.editor.section.hero.headline')],
                ['sub', t('demos.editor.section.hero.sub')],
                ['cta', t('demos.editor.section.hero.cta')],
              ] as [keyof HeroData, string][]).map(([field, label]) => (
                <div key={field} className="space-y-1">
                  <label className="text-xs font-medium text-muted-foreground" htmlFor={`hero-${field}`}>
                    {label}
                  </label>
                  <Input
                    id={`hero-${field}`}
                    value={hero[field]}
                    onChange={(e) => setHero((h) => ({ ...h, [field]: e.target.value }))}
                    placeholder={label}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Step 7 — Offers */}
          {stepIndex === 6 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.offers.title')}</h2>
              {offers.map((offer, idx) => (
                <div key={idx} className="space-y-1">
                  <label
                    className="text-xs font-medium text-muted-foreground"
                    htmlFor={`offer-${idx}`}
                  >
                    {t('demos.editor.section.offers.item', { n: idx + 1 })}
                  </label>
                  <Input
                    id={`offer-${idx}`}
                    value={offer}
                    onChange={(e) => {
                      const updated = [...offers];
                      updated[idx] = e.target.value;
                      setOffers(updated);
                    }}
                    placeholder={t('demos.editor.section.offers.itemPlaceholder')}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Step 8 — Testimonials */}
          {stepIndex === 7 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.testimonials.title')}</h2>
              {testimonials.map((quote, idx) => (
                <div key={idx} className="space-y-1">
                  <label
                    className="text-xs font-medium text-muted-foreground"
                    htmlFor={`testimonial-${idx}`}
                  >
                    {t('demos.editor.section.testimonials.quote', { n: idx + 1 })}
                  </label>
                  <Input
                    id={`testimonial-${idx}`}
                    value={quote}
                    onChange={(e) => {
                      const updated = [...testimonials];
                      updated[idx] = e.target.value;
                      setTestimonials(updated);
                    }}
                    placeholder={t('demos.editor.section.testimonials.quotePlaceholder')}
                  />
                </div>
              ))}
              {testimonials.length < 3 && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setTestimonials([...testimonials, ''])}
                >
                  {t('demos.editor.section.testimonials.addQuote')}
                </Button>
              )}
            </div>
          )}

          {/* Step 9 — Review + Generate */}
          {stepIndex === 8 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">{t('demos.studio.step.review.title')}</h2>
              <dl className="space-y-2 text-sm">
                <ReviewRow label={t('demos.studio.step.country.title')} value={country} />
                <ReviewRow label={t('demos.studio.step.industry.title')} value={industryKey} />
                <ReviewRow label={t('demos.list.column.businessName')} value={business.name} />
                <ReviewRow label={t('demos.studio.step.theme.title')} value={t(`demos.theme.${theme}.label`)} />
                <ReviewRow label={t('demos.studio.step.audience.title')} value={audience.mode} />
              </dl>
              {/* 114.6 — Optional opportunity link */}
              <div className="pt-2 space-y-1">
                <label className="text-sm font-medium">{t('demos.opportunityPicker.label')}</label>
                <div className="relative">
                  <Input
                    data-testid="opportunity-picker-input"
                    value={opportunitySearch}
                    placeholder={t('demos.opportunityPicker.placeholder')}
                    onChange={(e) => {
                      setOpportunitySearch(e.target.value);
                      if (!e.target.value) setOpportunityId('');
                      void searchOpportunities(e.target.value);
                    }}
                  />
                  {opportunitySuggestions.length > 0 && (
                    <ul className="absolute z-20 w-full rounded-md border bg-popover shadow-md mt-1">
                      {opportunitySuggestions.map((opp) => (
                        <li key={opp.id}>
                          <button
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm hover:bg-muted"
                            onClick={() => {
                              setOpportunityId(opp.id);
                              setOpportunitySearch(opp.name);
                              setOpportunitySuggestions([]);
                            }}
                          >
                            {opp.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {opportunityId && (
                  <p className="text-xs text-muted-foreground">
                    {t('demos.opportunityPicker.none') === 'No opportunity' ? '' : ''}
                    ID: {opportunityId}
                  </p>
                )}
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
            </div>
          )}
        </div>
      </div>

      {/* Footer navigation */}
      <div className="border-t border-border bg-background px-6 py-4 sticky bottom-0 z-10">
        <div className="max-w-2xl mx-auto flex justify-between">
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={stepIndex === 0}
          >
            {t('demos.studio.action.back')}
          </Button>

          {isLastStep ? (
            <Button
              onClick={handleGenerate}
              disabled={submitting || !canAdvance}
            >
              {submitting ? t('demos.studio.generate.loading') : t('demos.studio.action.generate')}
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={!canAdvance}>
              {t('demos.studio.action.next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-muted-foreground w-36 shrink-0">{label}</dt>
      <dd className="font-medium">{value || '—'}</dd>
    </div>
  );
}

function countryFlag(code: string): string {
  const flags: Record<string, string> = { PR: '🇵🇷', CO: '🇨🇴' };
  return flags[code.toUpperCase()] ?? '🌐';
}
